import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { createDOMRange, createRectsFromDOMRange } from "@lexical/selection";
import { $getSelection, $isRangeSelection, type LexicalEditor } from "lexical";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  SelectionActionPill,
  type SelectionAnchorRect,
} from "./SelectionActionPill";

export type SelectionActionPayload = {
  selectedText: string;
};

export type SelectionActionRenderPillArgs = {
  anchorRect: SelectionAnchorRect;
  selectedText: string;
  onActivate: () => void;
};

export type RichTextEditorSelectionAction = {
  enabled?: boolean;
  onSelection: (payload: SelectionActionPayload) => void;
  renderPill?: (args: SelectionActionRenderPillArgs) => React.ReactNode;
};

type SelectionActionPluginProps = RichTextEditorSelectionAction;

function readSelectionAnchor(
  editor: LexicalEditor,
): { selectedText: string; anchorRect: SelectionAnchorRect } | null {
  return editor.getEditorState().read(() => {
    const sel = $getSelection();
    if (!$isRangeSelection(sel) || sel.isCollapsed()) {
      return null;
    }
    const selectedText = sel.getTextContent().trim();
    if (!selectedText) {
      return null;
    }

    const domRange = createDOMRange(
      editor,
      sel.anchor.getNode(),
      sel.anchor.offset,
      sel.focus.getNode(),
      sel.focus.offset,
    );
    if (!domRange) {
      return null;
    }

    const rects = createRectsFromDOMRange(editor, domRange);
    if (!rects.length) {
      return null;
    }

    // First line’s rect — stable anchor for multi-line selections (common floating-ui pattern).
    const r = rects[0];
    return {
      selectedText,
      anchorRect: {
        left: r.left,
        top: r.top,
        width: r.width,
        height: r.height,
      },
    };
  });
}

export function SelectionActionPlugin({
  enabled = true,
  onSelection,
  renderPill,
}: SelectionActionPluginProps) {
  const [editor] = useLexicalComposerContext();
  const [ui, setUi] = useState<{
    selectedText: string;
    anchorRect: SelectionAnchorRect;
  } | null>(null);

  /** Primary pointer is down in the editor — hide pill until pointerup (avoid flash while dragging). */
  const pointerSelectingRef = useRef(false);

  const onSelectionRef = useRef(onSelection);
  onSelectionRef.current = onSelection;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const applySelectionUi = useCallback(() => {
    if (!enabledRef.current) {
      setUi(null);
      return;
    }
    if (pointerSelectingRef.current) {
      setUi(null);
      return;
    }
    const next = readSelectionAnchor(editor);
    setUi(next);
  }, [editor]);

  useEffect(() => {
    if (!enabled) {
      setUi(null);
    } else {
      applySelectionUi();
    }
  }, [enabled, applySelectionUi]);

  useEffect(() => {
    return editor.registerUpdateListener(() => {
      applySelectionUi();
    });
  }, [editor, applySelectionUi]);

  useEffect(() => {
    const onWindowPointerUp = () => {
      if (!pointerSelectingRef.current) return;
      pointerSelectingRef.current = false;
      queueMicrotask(() => {
        if (!enabledRef.current) {
          setUi(null);
          return;
        }
        const next = readSelectionAnchor(editor);
        setUi(next);
      });
    };

    const onRootPointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      pointerSelectingRef.current = true;
      setUi(null);
    };

    const onScroll = () => {
      applySelectionUi();
    };

    const unregisterRoot = editor.registerRootListener((rootEl, prevRootEl) => {
      prevRootEl?.removeEventListener("pointerdown", onRootPointerDown);
      prevRootEl?.removeEventListener("scroll", onScroll);
      rootEl?.addEventListener("pointerdown", onRootPointerDown);
      rootEl?.addEventListener("scroll", onScroll, { passive: true });
    });

    window.addEventListener("pointerup", onWindowPointerUp, true);
    return () => {
      window.removeEventListener("pointerup", onWindowPointerUp, true);
      unregisterRoot();
      const root = editor.getRootElement();
      root?.removeEventListener("pointerdown", onRootPointerDown);
      root?.removeEventListener("scroll", onScroll);
    };
  }, [editor, applySelectionUi]);

  const handleActivate = useCallback(() => {
    if (!ui) return;
    onSelectionRef.current({ selectedText: ui.selectedText });
    setUi(null);
  }, [ui]);

  if (!ui) {
    return null;
  }

  if (renderPill) {
    return renderPill({
      anchorRect: ui.anchorRect,
      selectedText: ui.selectedText,
      onActivate: handleActivate,
    });
  }

  return (
    <SelectionActionPill anchorRect={ui.anchorRect} onClick={handleActivate} />
  );
}
